import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import IcClock from '@/assets/ic_clock.svg';
import useStopwatch from '@/hooks/useStopWatch';
import useTrivia from '@/hooks/useTrivia';
import { ResponseDashTriviaInit } from '@/types/dashboard';

interface Props {
  triviaData: ResponseDashTriviaInit | undefined;
  isMobile?: boolean;
}

export const CountDownTrivias = React.memo(
  ({ children, triviaData: dataProps, isMobile }: PropsWithChildren<Props>) => {
    const { time, stop, start } = useStopwatch();
    const { updateTrivia, triviaData } = useTrivia();
    const [timeEnded, setTimeEnded] = useState(false);

    useEffect(() => {
      if (triviaData.start) {
        start();
      }
    }, [start, triviaData.start]);

    useEffect(() => {
      if (triviaData.stop) {
        stop();
      }
    }, [stop, triviaData.stop]);

    const total = useMemo(
      () => dataProps?.tiemposTriviaActual?.tiempoTotalDeParticipacion ?? 0,
      [dataProps?.tiemposTriviaActual?.tiempoTotalDeParticipacion]
    );

    useEffect(() => {
      if (total === time.inAbsoluteSeconds && !timeEnded) {
        updateTrivia({
          timeOut: true,
          stop: true,
          start: false,
        });
        setTimeEnded(true);
        stop();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stop, time.inAbsoluteSeconds, total, timeEnded]);

    const warning = useMemo(
      () => total - (dataProps?.tiemposTriviaActual?.tiempoInicioAlarma ?? 0),
      [total, dataProps?.tiemposTriviaActual?.tiempoInicioAlarma]
    );

    const isWarningTime = useMemo(() => {
      return warning <= time.inAbsoluteSeconds;
    }, [warning, time.inAbsoluteSeconds]);

    const formatTime = (value: number) => (value > 9 ? value : `0${value}`);

    const timeDisplay = useMemo(
      () =>
        `${formatTime(time.minutes)}:${formatTime(time.seconds)}:${
          time.centiseconds
        }`,
      [time.centiseconds, time.minutes, time.seconds]
    );

    const timerSection = useMemo(
      () => (
        <div className="flex items-center gap-2">
          <img alt="Clock" src={IcClock} />
          <span
            className={`text-lg font-bold ${isMobile ? 'w-20' : 'w-28'} ${
              isWarningTime ? 'text-gdt-status-red' : 'text-gdt-blue-header'
            }`}
          >
            {timeDisplay}
          </span>
        </div>
      ),
      [isMobile, isWarningTime, timeDisplay]
    );

    if (isMobile) {
      return (
        <>
          <div className="flex justify-between bg-white py-4 px-4 mx-5 rounded-lg mt-3 items-center">
            <div className="flex flex-col">
              <span className="font-semibold text-gdt-gray-2">
                {dataProps?.trv.nombre}
              </span>
              <span className="font-bold">
                {dataProps?.trv.nombreCategoria}
              </span>
            </div>
            {timerSection}
          </div>
          {children}
        </>
      );
    }

    return (
      <div className="flex justify-between bg-white py-4 px-8 items-center">
        {children}
        {timerSection}
      </div>
    );
  }
);

CountDownTrivias.displayName = 'CountDownTrivias';
